import { useState, useEffect } from 'react';
import '../css/deckgame.css';

function DeckGame() {
  const [gameState, setGameState] = useState('start');
  const [playerHealth, setPlayerHealth] = useState(20);
  const playerMaxHealth = 20;
  const [playerEnergy, setPlayerEnergy] = useState(3);
  const playerMaxEnergy = 3;
  const [deck, setDeck] = useState([]);
  const [hand, setHand] = useState([]);
  const [discardPile, setDiscardPile] = useState([]);
  
  const [enemyHealth, setEnemyHealth] = useState(15);
  const enemyMaxHealth = 15;
  const [enemyIntent, setEnemyIntent] = useState('');
  const enemyName = 'Shadow Mage';
  
  const [playerDamage, setPlayerDamage] = useState(0);
  const [playerBlock, setPlayerBlock] = useState(0);
  const [turnCount, setTurnCount] = useState(0);

  const cardTemplates = [
    { name: 'Arcane Bolt', damage: 6, cost: 1, type: 'attack', emoji: '✨' },
    { name: 'Arcane Bolt', damage: 6, cost: 1, type: 'attack', emoji: '✨' },
    { name: 'Barrier', block: 5, cost: 1, type: 'defense', emoji: '🔵' },
    { name: 'Barrier', block: 5, cost: 1, type: 'defense', emoji: '🔵' },
    { name: 'Inferno', damage: 10, cost: 2, type: 'attack', emoji: '🔥' },
    { name: 'Force Shield', block: 8, cost: 2, type: 'defense', emoji: '⚪' },
  ];

  const shuffleArray = (arr) => {
    const shuffled = [...arr];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const startGame = () => {
    const newDeck = cardTemplates.map((card, idx) => ({
      ...card,
      id: Math.random()
    }));
    
    const shuffled = shuffleArray(newDeck);
    setDeck(shuffled);
    setHand([]);
    setDiscardPile([]);
    setPlayerHealth(20);
    setPlayerDamage(0);
    setPlayerBlock(0);
    setEnemyHealth(15);
    setEnemyIntent('');
    setTurnCount(1);
    setGameState('playing');
  };

  const drawCards = (count) => {
    let currentDeck = [...deck];
    let currentDiscard = [...discardPile];
    let newHand = [...hand];

    for (let i = 0; i < count; i++) {
      if (currentDeck.length === 0 && currentDiscard.length === 0) break;
      
      if (currentDeck.length === 0) {
        currentDeck = shuffleArray(currentDiscard);
        currentDiscard = [];
      }

      if (currentDeck.length > 0) {
        newHand.push(currentDeck[0]);
        currentDeck = currentDeck.slice(1);
      }
    }

    setDeck(currentDeck);
    setDiscardPile(currentDiscard);
    setHand(newHand);
  };

  useEffect(() => {
    if (gameState === 'playing' && hand.length === 0 && deck.length > 0) {
      let newHand = [];
      let newDeck = [...deck];
      for (let i = 0; i < 5 && newDeck.length > 0; i++) {
        newHand.push(newDeck[0]);
        newDeck = newDeck.slice(1);
      }
      setHand(newHand);
      setDeck(newDeck);
    }
  }, [gameState, hand.length, deck.length]);

  const playCard = (cardToPlay, cardIndex) => {
    if (playerEnergy < cardToPlay.cost) return;

    let newDamage = playerDamage;
    let newBlock = playerBlock;

    if (cardToPlay.type === 'attack') {
      newDamage += cardToPlay.damage;
    } else {
      newBlock += cardToPlay.block;
    }

    // Remove card from hand by index
    const newHand = hand.filter((_, idx) => idx !== cardIndex);

    setPlayerEnergy(playerEnergy - cardToPlay.cost);
    setPlayerDamage(newDamage);
    setPlayerBlock(newBlock);
    setHand(newHand);
    setDiscardPile([...discardPile, cardToPlay]);
  };

  const endTurn = () => {
    // Apply damage
    const newEnemyHealth = enemyHealth - playerDamage;
    
    if (newEnemyHealth <= 0) {
      setEnemyHealth(newEnemyHealth);
      setGameState('victory');
      return;
    }

    // Enemy turn
    const enemyAttacks = Math.random() > 0.3;
    let newPlayerHealth = playerHealth;
    let newEnemyIntent = '';

    if (enemyAttacks) {
      const damage = Math.floor(Math.random() * 5) + 3;
      const actualDamage = Math.max(1, damage - playerBlock);
      newPlayerHealth -= actualDamage;
      newEnemyIntent = `Attack: ${damage}`;
    } else {
      newEnemyIntent = 'Defend';
    }

    if (newPlayerHealth <= 0) {
      setEnemyHealth(newEnemyHealth);
      setPlayerHealth(newPlayerHealth);
      setEnemyIntent(newEnemyIntent);
      setGameState('gameover');
      return;
    }

    // Update state for turn end
    setEnemyHealth(newEnemyHealth);
    setPlayerHealth(newPlayerHealth);
    setEnemyIntent(newEnemyIntent);

    // Start new turn after delay
    setTimeout(() => {
      setPlayerEnergy(playerMaxEnergy);
      setPlayerDamage(0);
      setPlayerBlock(0);
      setTurnCount(prevTurn => prevTurn + 1);
      setEnemyIntent('');

      // Draw new cards
      setHand(prevHand => {
        let currentDeck = [...deck];
        let currentDiscard = [...discardPile];
        const cardsNeeded = Math.max(0, 5 - prevHand.length);

        for (let i = 0; i < cardsNeeded; i++) {
          if (currentDeck.length === 0 && currentDiscard.length === 0) break;
          
          if (currentDeck.length === 0) {
            currentDeck = shuffleArray(currentDiscard);
            currentDiscard = [];
          }

          if (currentDeck.length > 0) {
            prevHand = [...prevHand, currentDeck[0]];
            currentDeck = currentDeck.slice(1);
          }
        }

        setDeck(currentDeck);
        setDiscardPile(currentDiscard);
        return prevHand;
      });
    }, 800);
  };

  return (
    <div className="gameContainer">
      {gameState === 'start' && (
        <div className="startScreen">
          <h1>⚡ Mage Duel ⚡</h1>
          <p>Unleash your magical powers and defeat the Shadow Mage!</p>
          <button onClick={startGame} className="btn-primary">Begin Duel</button>
        </div>
      )}

      {gameState === 'playing' && (
        <div className="gameLayout">
          <div className="topRow">
            <div className="enemyBox">
              <h3>🖤 {enemyName}</h3>
              <div className="healthBar">
                <div className="health" style={{ width: `${(enemyHealth / enemyMaxHealth) * 100}%` }}></div>
              </div>
              <p className="healthText">{Math.max(0, enemyHealth)}/{enemyMaxHealth}</p>
              {enemyIntent && <p className="intent">{enemyIntent}</p>}
            </div>

            <div className="statsBox">
              <div className="stat">
                <span className="label">Turn</span>
                <span className="value">{turnCount}</span>
              </div>
              <div className="stat">
                <span className="label">Deck</span>
                <span className="value">{deck.length}</span>
              </div>
            </div>

            <div className="playerBox">
              <h3>✨ Mage</h3>
              <div className="healthBar">
                <div className="health" style={{ width: `${(playerHealth / playerMaxHealth) * 100}%` }}></div>
              </div>
              <p className="healthText">{playerHealth}/{playerMaxHealth}</p>
              <div className="damageBlock">
                <span className="dmg">✨ {playerDamage}</span>
                <span className="blk">🔵 {playerBlock}</span>
              </div>
            </div>
          </div>

          <div className="middleRow">
            <div className="handBox">
              <p className="handLabel">Spellbook ({hand.length})</p>
              <div className="handCards">
                {hand.map((card, idx) => (
                  <button
                    key={idx}
                    className={`card ${card.type} ${playerEnergy < card.cost ? 'disabled' : ''}`}
                    onClick={() => playCard(card, idx)}
                    disabled={playerEnergy < card.cost}
                  >
                    <div className="cardEmoji">{card.emoji}</div>
                    <div className="cardName">{card.name}</div>
                    <div className="cardValue">
                      {card.type === 'attack' ? `${card.damage}` : `${card.block}`}
                    </div>
                    <div className="cardCost">{card.cost}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="bottomRow">
            <div className="energyBox">
              Mana: <span className="energyValue">{playerEnergy}/{playerMaxEnergy}</span>
            </div>
            <button onClick={endTurn} className="endTurnBtn">Cast Spells</button>
          </div>
        </div>
      )}

      {gameState === 'victory' && (
        <div className="endScreen victory">
          <h1>⚡ Victory! ⚡</h1>
          <p>You have defeated the Shadow Mage in {turnCount} turns!</p>
          <button onClick={startGame} className="btn-primary">Duel Again</button>
        </div>
      )}

      {gameState === 'gameover' && (
        <div className="endScreen defeat">
          <h1>🖤 Defeat 🖤</h1>
          <p>The Shadow Mage defeated you after {turnCount} turns.</p>
          <button onClick={startGame} className="btn-primary">Challenge Again</button>
        </div>
      )}
    </div>
  );
}

export default DeckGame;
